package dev.lingvo.shared

import kotlinx.serialization.Serializable

@Serializable
data class LocaleInfo(
    val code: String,
    val englishName: String,
    val nativeName: String,
    val flagEmoji: String,
)

val COMMON_LOCALES: List<LocaleInfo> = listOf(
    LocaleInfo("af", "Afrikaans", "Afrikaans", "🇿🇦"),
    LocaleInfo("ar", "Arabic", "العربية", "🇸🇦"),
    LocaleInfo("bg", "Bulgarian", "Български", "🇧🇬"),
    LocaleInfo("bn", "Bengali", "বাংলা", "🇧🇩"),
    LocaleInfo("ca", "Catalan", "Català", "🇪🇸"),
    LocaleInfo("cs", "Czech", "Čeština", "🇨🇿"),
    LocaleInfo("cy", "Welsh", "Cymraeg", "🇬🇧"),
    LocaleInfo("da", "Danish", "Dansk", "🇩🇰"),
    LocaleInfo("de", "German", "Deutsch", "🇩🇪"),
    LocaleInfo("el", "Greek", "Ελληνικά", "🇬🇷"),
    LocaleInfo("en", "English", "English", "🇺🇸"),
    LocaleInfo("en-GB", "English (United Kingdom)", "English (UK)", "🇬🇧"),
    LocaleInfo("en-US", "English (United States)", "English (US)", "🇺🇸"),
    LocaleInfo("es", "Spanish", "Español", "🇪🇸"),
    LocaleInfo("es-MX", "Spanish (Mexico)", "Español (México)", "🇲🇽"),
    LocaleInfo("et", "Estonian", "Eesti", "🇪🇪"),
    LocaleInfo("eu", "Basque", "Euskara", "🇪🇸"),
    LocaleInfo("fa", "Persian", "فارسی", "🇮🇷"),
    LocaleInfo("fi", "Finnish", "Suomi", "🇫🇮"),
    LocaleInfo("fil", "Filipino", "Filipino", "🇵🇭"),
    LocaleInfo("fr", "French", "Français", "🇫🇷"),
    LocaleInfo("fr-CA", "French (Canada)", "Français (Canada)", "🇨🇦"),
    LocaleInfo("ga", "Irish", "Gaeilge", "🇮🇪"),
    LocaleInfo("gl", "Galician", "Galego", "🇪🇸"),
    LocaleInfo("gu", "Gujarati", "ગુજરાતી", "🇮🇳"),
    LocaleInfo("he", "Hebrew", "עברית", "🇮🇱"),
    LocaleInfo("hi", "Hindi", "हिन्दी", "🇮🇳"),
    LocaleInfo("hr", "Croatian", "Hrvatski", "🇭🇷"),
    LocaleInfo("hu", "Hungarian", "Magyar", "🇭🇺"),
    LocaleInfo("id", "Indonesian", "Bahasa Indonesia", "🇮🇩"),
    LocaleInfo("is", "Icelandic", "Íslenska", "🇮🇸"),
    LocaleInfo("it", "Italian", "Italiano", "🇮🇹"),
    LocaleInfo("ja", "Japanese", "日本語", "🇯🇵"),
    LocaleInfo("km", "Khmer", "ភាសាខ្មែរ", "🇰🇭"),
    LocaleInfo("kn", "Kannada", "ಕನ್ನಡ", "🇮🇳"),
    LocaleInfo("ko", "Korean", "한국어", "🇰🇷"),
    LocaleInfo("lt", "Lithuanian", "Lietuvių", "🇱🇹"),
    LocaleInfo("lv", "Latvian", "Latviešu", "🇱🇻"),
    LocaleInfo("mk", "Macedonian", "Македонски", "🇲🇰"),
    LocaleInfo("ml", "Malayalam", "മലയാളം", "🇮🇳"),
    LocaleInfo("mr", "Marathi", "मराठी", "🇮🇳"),
    LocaleInfo("ms", "Malay", "Bahasa Melayu", "🇲🇾"),
    LocaleInfo("my", "Burmese", "မြန်မာ", "🇲🇲"),
    LocaleInfo("nb", "Norwegian Bokmål", "Norsk bokmål", "🇳🇴"),
    LocaleInfo("nl", "Dutch", "Nederlands", "🇳🇱"),
    LocaleInfo("no", "Norwegian", "Norsk", "🇳🇴"),
    LocaleInfo("pa", "Punjabi", "ਪੰਜਾਬੀ", "🇮🇳"),
    LocaleInfo("pl", "Polish", "Polski", "🇵🇱"),
    LocaleInfo("pt", "Portuguese", "Português", "🇵🇹"),
    LocaleInfo("pt-BR", "Portuguese (Brazil)", "Português (Brasil)", "🇧🇷"),
    LocaleInfo("pt-PT", "Portuguese (Portugal)", "Português (Portugal)", "🇵🇹"),
    LocaleInfo("ro", "Romanian", "Română", "🇷🇴"),
    LocaleInfo("ru", "Russian", "Русский", "🇷🇺"),
    LocaleInfo("sk", "Slovak", "Slovenčina", "🇸🇰"),
    LocaleInfo("sl", "Slovenian", "Slovenščina", "🇸🇮"),
    LocaleInfo("sq", "Albanian", "Shqip", "🇦🇱"),
    LocaleInfo("sr", "Serbian", "Српски", "🇷🇸"),
    LocaleInfo("sv", "Swedish", "Svenska", "🇸🇪"),
    LocaleInfo("sw", "Swahili", "Kiswahili", "🇰🇪"),
    LocaleInfo("ta", "Tamil", "தமிழ்", "🇮🇳"),
    LocaleInfo("te", "Telugu", "తెలుగు", "🇮🇳"),
    LocaleInfo("th", "Thai", "ไทย", "🇹🇭"),
    LocaleInfo("tr", "Turkish", "Türkçe", "🇹🇷"),
    LocaleInfo("uk", "Ukrainian", "Українська", "🇺🇦"),
    LocaleInfo("ur", "Urdu", "اردو", "🇵🇰"),
    LocaleInfo("vi", "Vietnamese", "Tiếng Việt", "🇻🇳"),
    // **The bare language, not only its regions.** Android's `values-zh/` and a plain `zh.json` are
    // ordinary — the region is the exception, not the rule — and without an entry the lookup fell
    // through to its own fallback and rendered the raw code, so a language chip read "zh" and a
    // locale column read "Pt". Listed before the regional forms so the generic answer is the one a
    // bare code finds.
    LocaleInfo("zh", "Chinese", "中文", "🇨🇳"),
    LocaleInfo("zh-CN", "Chinese (Simplified)", "简体中文", "🇨🇳"),
    LocaleInfo("zh-HK", "Chinese (Hong Kong)", "繁體中文（香港）", "🇭🇰"),
    LocaleInfo("zh-TW", "Chinese (Traditional)", "繁體中文", "🇹🇼"),
)

private val commonLocalesByCode: Map<String, LocaleInfo> =
    COMMON_LOCALES.associateBy { it.code.lowercase() }

/**
 * The flag for a locale, and only where a flag is a fact.
 *
 * **A flag is a country; most of these codes are languages.** `pt` is not Portugal — roughly four in
 * five Portuguese speakers are Brazilian. `ar` is not Saudi Arabia; it is twenty-two countries. `en`
 * is not the United States. The table below assigns one anyway, because a `LocaleInfo` needs
 * something in the field, and the interface was printing it: a Brazilian developer opening their
 * `pt` strings was shown 🇵🇹, and a product whose entire job is that distinction was making the
 * oldest mistake in localization.
 *
 * A region-qualified code has already answered the question — `pt-BR`, `es-MX`, `en-GB` — and there
 * the flag is true, useful, and worth keeping. Everywhere else the language name and the code carry
 * the identity, which is what a developer writes in `lingvo.yaml` regardless.
 *
 * Returns the flag with a trailing space so a call site can prefix it unconditionally, or an empty
 * string that collapses to nothing.
 */
fun localeFlagPrefix(code: String): String {
    val info = localeInfo(code)
    return if (info.code.contains('-')) "${info.flagEmoji} " else ""
}

fun localeInfo(code: String): LocaleInfo {
    val canonicalCode = canonicalizeLocaleCode(code)
    return commonLocalesByCode[canonicalCode.lowercase()] ?: LocaleInfo(
        code = canonicalCode,
        englishName = canonicalCode,
        nativeName = canonicalCode,
        // Region subtag first, then the language's default region, then the globe. The middle step
        // is what stops an uncatalogued language sitting in a list of flags wearing a globe.
        // Region subtag, then the catalogued base language, then the language's default region,
        // then the globe.
        //
        // **The middle step is the one that was missing.** `zh-Hans` has no region — a script
        // subtag is not a region — and `zh` is one of the 61 catalogued languages, so it is
        // deliberately absent from the default map. Both steps missed and a Chinese target showed
        // a globe next to `pt-BR`'s flag in the same table, which is the inconsistency this whole
        // fallback exists to remove. Reading the base language's own catalogued flag covers every
        // script- and variant-qualified tag: `zh-Hans`, `sr-Latn`, `de-1996`.
        flagEmoji = regionFromLocale(canonicalCode)?.let(::flagEmojiForRegion)
            ?: commonLocalesByCode[canonicalCode.substringBefore('-').lowercase()]?.flagEmoji
            ?: DEFAULT_REGION_BY_LANGUAGE[canonicalCode.substringBefore('-').lowercase()]
                ?.let(::flagEmojiForRegion)
            ?: GLOBE_EMOJI,
    )
}

/**
 * The CLDR cardinal plural categories a locale actually uses.
 *
 * Grouped from CLDR 48 `plurals.xml`. Getting this wrong is not cosmetic: a translator shown a
 * category their language does not use writes a form that is never selected, and a missing category
 * leaves a gap that renders as an empty string (or `(null)` on Apple platforms) in a shipped app.
 */
fun pluralCategoriesFor(locale: String): Set<PluralCategory> {
    val language = canonicalizeLocaleCode(locale).substringBefore('-')
    return when (language) {
        // zero, one, two, few, many, other
        "ar", "ars", "cy", "kw" -> setOf(
            PluralCategory.ZERO,
            PluralCategory.ONE,
            PluralCategory.TWO,
            PluralCategory.FEW,
            PluralCategory.MANY,
            PluralCategory.OTHER,
        )
        // one, two, few, many, other
        "br", "ga", "gv", "mt", "sgs" -> setOf(
            PluralCategory.ONE,
            PluralCategory.TWO,
            PluralCategory.FEW,
            PluralCategory.MANY,
            PluralCategory.OTHER,
        )
        // one, two, few, other
        "dsb", "gd", "hsb", "sl" -> setOf(
            PluralCategory.ONE,
            PluralCategory.TWO,
            PluralCategory.FEW,
            PluralCategory.OTHER,
        )
        // one, few, many, other
        "be", "cs", "lt", "pl", "ru", "sk", "uk" -> setOf(
            PluralCategory.ONE,
            PluralCategory.FEW,
            PluralCategory.MANY,
            PluralCategory.OTHER,
        )
        // zero, one, other
        "blo", "cv", "ksh", "lag", "lv", "prg" ->
            setOf(PluralCategory.ZERO, PluralCategory.ONE, PluralCategory.OTHER)
        // one, two, other
        "he", "iw", "iu", "naq", "sat", "se", "sma", "smi", "smj", "smn", "sms" ->
            setOf(PluralCategory.ONE, PluralCategory.TWO, PluralCategory.OTHER)
        // one, few, other
        "bs", "hr", "mo", "ro", "sh", "shi", "sr" ->
            setOf(PluralCategory.ONE, PluralCategory.FEW, PluralCategory.OTHER)
        // one, many, other — Spanish, Italian, Catalan and Portuguese gained `many` in CLDR 41/42
        "ca", "es", "fr", "it", "lld", "pt", "scn", "vec" ->
            setOf(PluralCategory.ONE, PluralCategory.MANY, PluralCategory.OTHER)
        // other only — no plural distinction at all
        "bm", "bo", "dz", "hnj", "id", "ig", "ii", "in", "ja", "jbo", "jv", "jw",
        "kde", "kea", "km", "ko", "lkt", "lo", "ms", "my", "nqo", "osa", "root",
        "und", "sah", "ses", "sg", "su", "th", "to", "tpi", "vi", "wo", "yo",
        "yue", "zh",
        -> setOf(PluralCategory.OTHER)
        // one, other — the CLDR default, including en/de/tr/gl/hi/sw
        else -> setOf(PluralCategory.ONE, PluralCategory.OTHER)
    }
}

/**
 * The gettext `Plural-Forms` rule for a locale, or null when this codebase will not claim one.
 *
 * This is an explicit table of gettext convention, not a derivation from [pluralCategoriesFor].
 * gettext numbers forms with a locale-specific C expression, while CLDR names categories. CLDR 48
 * now gives Catalan, Spanish, French, Italian and Portuguese a `many` category for compact forms
 * such as "1,2 millions", but their gettext catalogues conventionally still use two indices. The
 * systems genuinely disagree, so using the CLDR set here would refuse those large languages.
 */
fun gettextPluralRule(locale: String): String? {
    val canonicalLocale = canonicalizeLocaleCode(locale)
    val language = canonicalLocale.substringBefore('-')
    val regionOverride = GETTEXT_RULES_BY_LOCALE[canonicalLocale]
    return regionOverride ?: when {
        // Icelandic is two-form but its boundary is not `n != 1`: 21, 31, 41 take the singular.
        // Claiming the ordinary rule here would write a header that selects the wrong form for
        // every such number, which is the exact failure this function exists to prevent.
        language == "is" -> "nplurals=2; plural=(n%10!=1 || n%100==11);"
        language in GETTEXT_OTHER_ONLY_LANGUAGES -> GETTEXT_ONE_FORM_RULE
        language in GETTEXT_ZERO_SINGULAR_LANGUAGES -> GETTEXT_ZERO_SINGULAR_RULE
        language in GETTEXT_ORDINARY_TWO_FORM_LANGUAGES -> GETTEXT_ORDINARY_TWO_FORM_RULE
        else -> null
    }
}

private val GETTEXT_RULES_BY_LOCALE = mapOf(
    // Brazilian Portuguese follows the French-style rule; European Portuguese does not.
    "pt-BR" to "nplurals=2; plural=(n > 1);",
    "pt-PT" to "nplurals=2; plural=(n != 1);",
)

private const val GETTEXT_ONE_FORM_RULE = "nplurals=1; plural=0;"
private const val GETTEXT_ZERO_SINGULAR_RULE = "nplurals=2; plural=(n > 1);"
private const val GETTEXT_ORDINARY_TWO_FORM_RULE = "nplurals=2; plural=(n != 1);"

private val GETTEXT_OTHER_ONLY_LANGUAGES = setOf(
    "bm", "bo", "dz", "hnj", "id", "ig", "ii", "in", "ja", "jbo", "jv", "jw",
    "kde", "kea", "km", "ko", "lkt", "lo", "ms", "my", "nqo", "osa", "root", "sah",
    "ses", "sg", "su", "th", "to", "tpi", "und", "vi", "wo", "yo", "yue", "zh",
)

private val GETTEXT_ZERO_SINGULAR_LANGUAGES = setOf(
    // These gettext conventions treat zero as singular. Keep this independent from CLDR, whose
    // category names and counts are intentionally modeled by pluralCategoriesFor above.
    "fa", "fil", "fr", "hy", "kab",
)

private val GETTEXT_ORDINARY_TWO_FORM_LANGUAGES = setOf(
    "af", "an", "ast", "az", "bg", "bn", "ca", "da", "de", "el", "en", "eo", "es",
    "et", "eu", "fi", "gl", "gu", "he", "hi", "hu", "it", "ka", "kk", "kn",
    "ml", "mn", "mr", "nb", "nl", "nn", "no", "pa", "pt", "sv", "sw", "ta", "te",
    "tr", "ur", "uz", "xh", "yi", "zu",
)

private fun canonicalizeLocaleCode(code: String): String {
    val subtags = code
        .trim()
        .replace('_', '-')
        .split('-')
        .filter { it.isNotEmpty() }
    if (subtags.isEmpty()) return "und"

    val result = mutableListOf<String>()
    var inExtension = false
    subtags.forEachIndexed { index, subtag ->
        val canonicalSubtag = when {
            index == 0 -> subtag.lowercase()
            subtag.length == 1 -> {
                inExtension = true
                subtag.lowercase()
            }
            inExtension -> subtag.lowercase()
            subtag.length == 4 && subtag.all { it.isAsciiLetter() } ->
                subtag.lowercase().replaceFirstChar { it.uppercaseChar() }
            subtag.length == 2 && subtag.all { it.isAsciiLetter() } -> subtag.uppercase()
            subtag.length == 3 && subtag.all { it in '0'..'9' } -> subtag
            else -> subtag.lowercase()
        }
        result += canonicalSubtag
    }
    return result.joinToString("-")
}

/**
 * The region whose flag a reader associates with a language that names no region of its own.
 *
 * **Why this list exists.** `localeInfo` derives a flag from a region subtag — `de-AT` gives 🇦🇹 —
 * and falls back to a globe when there is none. The 61 catalogued languages carry a hand-assigned
 * flag, so `de` shows 🇩🇪 and `sw` shows 🇹🇿; every *other* bare code showed 🌐. A project list mixing
 * flags with globes reads as a bug rather than as a distinction, which is what it looked like.
 *
 * **A language is not a country, and this mapping is a compromise, not a fact.** Arabic is official
 * in 25 countries; Spanish in 20; Quechua spans three. One flag cannot represent any of them. The
 * rule here is the same one the catalogue's own rows already follow — the largest speaker base —
 * chosen for recognisability rather than correctness, because the alternative on offer was a globe
 * that recognises nothing.
 *
 * The globe remains for anything this does not cover: a private-use tag, a constructed code, a
 * three-letter ISO 639-3 language a customer imports. That case cannot be closed, only made rare.
 */
private val DEFAULT_REGION_BY_LANGUAGE: Map<String, String> = mapOf(
    "aa" to "ET",
    "ab" to "GE",
    "ae" to "IR",
    "ak" to "GH",
    "am" to "ET",
    "an" to "ES",
    "as" to "IN",
    "av" to "RU",
    "ay" to "BO",
    "az" to "AZ",
    "ba" to "RU",
    "be" to "BY",
    "bh" to "IN",
    "bi" to "VU",
    "bm" to "ML",
    "bo" to "CN",
    "br" to "FR",
    "bs" to "BA",
    "ce" to "RU",
    "ch" to "GU",
    "co" to "FR",
    "cr" to "CA",
    "cu" to "BG",
    "cv" to "RU",
    "dv" to "MV",
    "dz" to "BT",
    "ee" to "GH",
    "eo" to "PL",
    "ff" to "SN",
    "fj" to "FJ",
    "fo" to "FO",
    "fy" to "NL",
    "gd" to "GB",
    "gn" to "PY",
    "gv" to "IM",
    "ha" to "NG",
    "ho" to "PG",
    "ht" to "HT",
    "hy" to "AM",
    "hz" to "NA",
    "ia" to "FR",
    "ie" to "AT",
    "ig" to "NG",
    "ii" to "CN",
    "ik" to "US",
    "io" to "FR",
    "iu" to "CA",
    "jv" to "ID",
    "ka" to "GE",
    "kb" to "DZ",
    "kg" to "CD",
    "ki" to "KE",
    "kj" to "NA",
    "kk" to "KZ",
    "kl" to "GL",
    "kr" to "NG",
    "ks" to "IN",
    "ku" to "IQ",
    "kv" to "RU",
    "kw" to "GB",
    "ky" to "KG",
    "la" to "VA",
    "lb" to "LU",
    "lg" to "UG",
    "li" to "NL",
    "ln" to "CD",
    "lo" to "LA",
    "lu" to "CD",
    "mg" to "MG",
    "mh" to "MH",
    "mi" to "NZ",
    "mn" to "MN",
    "mt" to "MT",
    "na" to "NR",
    "nd" to "ZW",
    "ne" to "NP",
    "ng" to "NA",
    "nn" to "NO",
    "nr" to "ZA",
    "nso" to "ZA",
    "nv" to "US",
    "ny" to "MW",
    "oc" to "FR",
    "oj" to "CA",
    "om" to "ET",
    "or" to "IN",
    "os" to "GE",
    "pi" to "IN",
    "ps" to "AF",
    "qu" to "PE",
    "rm" to "CH",
    "rn" to "BI",
    "rw" to "RW",
    "sa" to "IN",
    "sc" to "IT",
    "sd" to "PK",
    "se" to "NO",
    "sg" to "CF",
    "si" to "LK",
    "sm" to "WS",
    "sn" to "ZW",
    "so" to "SO",
    "ss" to "SZ",
    "st" to "LS",
    "su" to "ID",
    "tg" to "TJ",
    "ti" to "ER",
    "tk" to "TM",
    "tl" to "PH",
    "tlh" to "US",
    "tn" to "BW",
    "to" to "TO",
    "ts" to "ZA",
    "tt" to "RU",
    "tw" to "GH",
    "ty" to "PF",
    "ug" to "CN",
    "uz" to "UZ",
    "ve" to "ZA",
    "vo" to "DE",
    "wa" to "BE",
    "wo" to "SN",
    "xh" to "ZA",
    "yi" to "IL",
    "yo" to "NG",
    "za" to "CN",
    "zu" to "ZA",
)

private fun regionFromLocale(code: String): String? {
    val subtags = code.split('-')
    for (subtag in subtags.drop(1)) {
        when {
            subtag.length == 1 -> return null
            subtag.length == 2 && subtag.all { it.isAsciiLetter() } -> return subtag
            subtag.length == 3 && subtag.all { it.isAsciiLetter() } -> continue
            subtag.length == 3 && subtag.all { it in '0'..'9' } -> return null
            subtag.length == 4 && subtag.all { it.isAsciiLetter() } -> continue
            else -> return null
        }
    }
    return null
}

private fun flagEmojiForRegion(region: String): String {
    val uppercaseRegion = region.uppercase()
    return regionalIndicator(uppercaseRegion[0]) + regionalIndicator(uppercaseRegion[1])
}

private fun regionalIndicator(letter: Char): String {
    val codePoint = REGIONAL_INDICATOR_A + (letter.code - 'A'.code)
    val offset = codePoint - SUPPLEMENTARY_CODE_POINT_START
    val highSurrogate = (HIGH_SURROGATE_START + (offset shr SURROGATE_SHIFT)).toChar()
    val lowSurrogate = (LOW_SURROGATE_START + (offset and SURROGATE_MASK)).toChar()
    return highSurrogate.toString() + lowSurrogate
}

private fun Char.isAsciiLetter(): Boolean = this in 'A'..'Z' || this in 'a'..'z'

private const val GLOBE_EMOJI = "🌐"
private const val REGIONAL_INDICATOR_A = 0x1F1E6
private const val SUPPLEMENTARY_CODE_POINT_START = 0x10000
private const val HIGH_SURROGATE_START = 0xD800
private const val LOW_SURROGATE_START = 0xDC00
private const val SURROGATE_SHIFT = 10
private const val SURROGATE_MASK = 0x3FF
